import React, { useMemo, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import * as PR from '../../prime-react/index';
import { DateFormat, dompurifyText, formatTime, trimFileName } from '../../utils/reuse';
import { useDispatch, useSelector } from 'react-redux';
import { deleteReport, downloadReportZipById, getFileDetails, getReportById } from '../../services/api';
import "./Reports.scss";
import { ErrorMessage, useToast } from '../../context/ToastContext';
import { getImageForReport } from '../../utils/reportImages';
import JSZip from 'jszip';
import { saveAs } from "file-saver";
import { generateScreenContentPDF } from '../../utils/downloadPageContent';
import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import { PDFViewer } from '../../utils/pdfPreview';
import { authActions } from '../../store/auth';
import { downloadAttachment } from '../../utils/downloadAttachment';

const ReportDetail = () => {
    const { showToast, clearError } = useToast();
    const { search } = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(search);
    const reportID = queryParams.get('id');
    const [report, setReport] = useState(null);
    const dispatch = useDispatch();
    const sessionData = useSelector(state => state.auth.sessionData);
    const headers = useMemo(() => ({ sessionid: sessionData.sessionId }), [sessionData.sessionId]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const isAnalyst = useSelector(state => state.auth.role) === 'analyst';
    const isRegulator = useSelector(state => state.auth.role) === 'regulator';
    const locationID = sessionData.locationID;
    const deleteReportError = 'delete_report_error';
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadingKey, setDownloadingKey] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [previewType, setPreviewType] = useState(null); // "pdf" or "html"
    const [showModal, setShowModal] = useState(false);
    const isUSRegulator = isRegulator && locationID === 238;
    useEffect(() => {
        getReportById(reportID, headers, dispatch, (response) => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                if (responseData && responseData?.length > 0) {
                    setReport(responseData[0]);
                    const allowedGroupPermissions = responseData[0]?.groupPermission;
                    dispatch(authActions.updateGroupPermission(allowedGroupPermissions));
                } else {
                    navigate('/reports');
                }
            } else {
                navigate('/reports');
            }
        });
    }, [reportID, headers, dispatch, showToast, navigate]);

    if (!report) return <div className="loading"><i className="pi pi-spin pi-spinner"></i></div>;

    const fetchFileDetails = (file) => {
        downloadAttachment({
            file,
            id: reportID,
            type: "report",
            headers,
            dispatch,
            showToast,
        });
    };

    const formatPreviewDate = (val) => {
        if (typeof val !== "string") return val; // skip empty or non-date strings
        const parts = val.split(/[/-]/); // Try parsing as date
        if (parts.length < 3) return val;
        let [d, m, y] = parts.map(p => p.padStart(2, "0"));
        if (y.length === 2) y = "20" + y; // Handle 2-digit year like "25"
        return `${d}/${m}/${y}`;
      };
      
    const filePreviewHandler = (key, fileName, setFilePreview, setShowModal, setPreviewType) => {
        setDownloadingKey(key);
        getFileDetails(key, headers, dispatch, async response => {
            if (response.result === 'SUCCESS') {
                const fileData = response.data;
                const fileBlob = new Blob([fileData]);
                const arrayBuffer = await fileBlob.arrayBuffer();
                const fileExtension = fileName.split('.').pop().toLowerCase();
                let previewContent = null;
                switch (fileExtension) {
                    case 'pdf':
                        previewContent = URL.createObjectURL(fileData);
                        setPreviewType('pdf');
                        break;
                    case 'xlsx':
                        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];
                        // Convert sheet to JSON to detect empty columns
                        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, cellDates: true, raw: false });

                        // Filter out empty columns
                        const filtered = json.map(row => {
                            const nonEmptyIndexes = row.map((cell, index) => {
                                const formattedCell = formatPreviewDate(cell);
                                return { cell: formattedCell, index };
                            })
                                .filter(({ cell }) => cell !== null && cell !== undefined && cell !== '')
                                .map(({ index }) => index);

                            const maxIndex = Math.max(...nonEmptyIndexes, 0);
                            return row.map(formatPreviewDate).slice(0, maxIndex + 1);
                        });
                        const cleanedSheet = XLSX.utils.aoa_to_sheet(filtered);
                        const rawHtml = XLSX.utils.sheet_to_html(cleanedSheet);
                        const styledHtml = `
                        <style>
                          table {
                            border-collapse: collapse;
                            width: auto;
                            table-layout: auto;
                            white-space: nowrap;
                            position: relative;
                            right: 0.65rem;
                          }
                          td, th {
                            border: 1px solid #ccc;
                            padding: 8px;
                            white-space: nowrap;
                          }
                          tr:first-child td {
                            font-weight: bold;
                            background: #f9f9f9;
                          }
                        </style>
                        ${rawHtml}
                      `;
                        setPreviewType('html');
                        previewContent = styledHtml;
                        break;
                    case 'docx':
                        setPreviewType('html');
                        previewContent = `<div id="docx-preview"></div>`;
                        
                        // Wait for modal DOM to render and then render the docx
                        setTimeout(() => {
                            const container = document.getElementById('docx-preview');
                            renderAsync(arrayBuffer, container);
                        }, 100); // Give time for modal DOM to mount
                        break;
                    default:
                        showToast('warn', `Unsupported file format: .${fileExtension}`);
                        return;
                }
                setFilePreview(previewContent);
                setShowModal(true);

                const disableContextMenu = (e) => e.preventDefault();
                document.addEventListener('contextmenu', disableContextMenu);
            
                return () => {
                    document.removeEventListener('contextmenu', disableContextMenu);
                };
            } else {
                const error = response?.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        });
    };
    
    const handleClose = () => {
        if (previewType === 'pdf' && filePreview) {
            URL.revokeObjectURL(filePreview);
        }
        setFilePreview(null);
        setPreviewType(null);
        setShowModal(false);
        setIsDownloading(false);
        setDownloadingKey(null);
    };

    const deleteReportHandler = (id) => {
        clearError(deleteReportError)
        deleteReport(id, headers, dispatch, (response) => {
            if (response.result === "SUCCESS") {
                navigate("/reports");
            } else {
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary, deleteReportError);
            }
        });
    };

    // Function to safely parse JSON
    const parseAttachments = (fileAttachment) => {
        try {
            return JSON.parse(fileAttachment || "[]");
        } catch (error) {
            showToast("error", "Attachments data is corrupted or missing.");
            return null;
        }
    };

    // Utility function to fetch and add files to the ZIP
    const addFileToZip = async (zip, key, fileName) => {
        return new Promise((resolve, reject) => {
            getFileDetails(key, headers, dispatch, (response) => {
                if (response.result === "SUCCESS") {
                    zip.file(fileName, response.data);
                    resolve();
                } else {
                    const error = response?.error;
                    showToast(error.severity, error?.errorMsg || error?.summary);
                    reject(new Error(`Failed to fetch file: ${fileName}`));
                }
            });
        });
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        const zip = new JSZip();
        // Parse file attachments
        const attachments = parseAttachments(report?.fileAttachment);
        if (!attachments) {
            setIsDownloading(false);
            return;
        }
        try {
            // Add all attachments to the ZIP
            for (const { key, name } of attachments) { 
                await addFileToZip(zip, key, name);
            }
            // Add the generated PDF to the ZIP
            const pdfBlob = await generateScreenContentPDF(report);
            zip.file(`${report.title.replace(/ /g, "_")}.pdf`, pdfBlob);
            // Generate the ZIP file and trigger download
            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(zipBlob, `${report.title.replace(/ /g, "_")}_attachments.zip`);
            downloadReportZipById(reportID, headers, dispatch, (response) => {
                if (response.result !== "SUCCESS") {
                    const error = response?.error;
                    showToast(error.severity, error?.errorMsg || error?.summary);
                }
            })
        } catch (error) {
            showToast("error", "Error during ZIP creation or file fetching:", error);
        } finally {
            setIsDownloading(false);
        }
    };

    const fromAuditLog = queryParams.get("from") === "audit-log";

    return (
        <Layout backBtn={fromAuditLog ? "audit-log" : "reports"} backBtnLabel={fromAuditLog ? "Audit Log" : "Reports"}>
            <section className="news-tab news-details-section general-news-section reports-section">
                <div className="flex align-items-center justify-content-between mb-4">
                    <div className="left">
                        <h1 className='capitalize'>{report?.title}</h1>
                    </div>
                    {isAnalyst &&
                        <div className="right flex gap-2">
                            <PR.Button
                                label="Edit"
                                icon="pi pi-pencil"
                                className="action-buttons"
                                onClick={() =>
                                    navigate(`/edit-report?id=${reportID}`)
                                }
                            />
                            <PR.Button
                                label="Delete"
                                icon="pi pi-trash"
                                className="action-buttons"
                                onClick={() => setShowDeleteDialog(true)}
                            />
                        </div>
                    }
                </div>
                <div className="grid" id="pdfContent">
                    <div className="col-12">
                        <div className="new-list-section">
                            <div className="list-wrapper">
                                <div className="news-list preview-report" id="report-content">
                                    <div className="relative">
                                        <PR.Image src={getImageForReport(report)} alt={report?.title} className="report-img" />
                                        <div className="overlay">
                                            <h2>{report?.type}</h2>
                                        </div>
                                    </div>
                                    <div className="attachments report-attachments flex align-items-start justify-content-between mt-3">
                                        <div className="flex flex-wrap gap-2">
                                            {report?.fileAttachment && (
                                                JSON.parse(report?.fileAttachment)?.map((file) => (
                                                    <PR.Button
                                                        key={file.key}
                                                        icon={(downloadingKey === file.key) ? "pi pi-spin pi-spinner" : "pi pi-book"}
                                                        label={trimFileName(file.name)}
                                                        className="download-button hide-on-download"
                                                        onClick={() => ((!isUSRegulator && report?.groupPermission.includes('export')) || (isUSRegulator && (report?.enableDownload || report?.groupPermission.includes('export'))) || !isRegulator) ? fetchFileDetails(file) : filePreviewHandler(file.key, file.name, setFilePreview, setShowModal, setPreviewType) }
                                                    />
                                                ))
                                            )}
                                        </div>
                                        <div className="flex">
                                            {((!isUSRegulator && report?.groupPermission.includes('export')) || (isUSRegulator && (report?.enableDownload || report?.groupPermission.includes('export'))) || !isRegulator) && <PR.Button
                                                icon={isDownloading ? "pi pi-spin pi-spinner" : "pi pi-download"}
                                                label={isDownloading ? "Downloading..." : "Download Report"}
                                                className={`${isRegulator && 'regulator'} save-button hide-on-download`}
                                                onClick={handleDownload}
                                                disabled={isDownloading}
                                            />}
                                        </div>
                                    </div>
                                    <div className="col-12 p-0 mt-3">
                                        <div className="content" dangerouslySetInnerHTML={{ __html: dompurifyText(report?.description) }} />
                                        <div className="attachments flex gap-3 mt-3">
                                            <span className="type-info">Report Type: {report?.reportType}</span>
                                            {report?.alertID && <span className="type-info">Alert ID: {report?.alertID}</span>}
                                            {report?.sportName && <span className="type-info">Sport: {report?.sportName}</span>}
                                        </div>
                                        <div className="card mt-5">
                                            <ul className="list-none gap-4 flex align-items-center justify-content-between mb-0">
                                                <li><i className="pi pi-calendar"></i> <span className="author">{DateFormat(report?.createdAt)} {formatTime(report?.createdAt)}</span></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <PR.Dialog
                visible={showDeleteDialog}
                header="Delete report"
                modal
                closable={false}
                footer={
                    <>
                        <ErrorMessage id={deleteReportError} />
                        <PR.Button
                            label="Cancel"
                            className="p-button-text custom-cancel-button"
                            onClick={() => { clearError(deleteReportError); setShowDeleteDialog(false) }}
                        />
                        <PR.Button
                            label="Delete"
                            className="p-button-danger custom-delete-button"
                            onClick={() => deleteReportHandler(reportID)}
                        />
                    </>
                }
            >
                <div className="confirmation-content">
                    <span>Are you sure you want to delete this report?</span>
                </div>
            </PR.Dialog>

            <PR.Dialog
                header="File Preview"
                visible={showModal}
                style={{ width: '80vw' }}
                onHide={handleClose}
                modal
                maskClassName='pdf-preview-modal'
            >
                {previewType === 'pdf' && (
                    <div 
                        className="no-select"
                        style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        overflow: 'auto',
                        padding: '1rem',
                        background: 'gray',
                      }}>
                        <PDFViewer file={filePreview} />
                    </div>
                )}

                {previewType === 'html' && (
                    <div
                    className="no-select"
                    style={{ maxHeight: '650px'}}
                    dangerouslySetInnerHTML={{ __html: filePreview }}
                    />
                )}
            </PR.Dialog>
        </Layout>
    );
};

export default ReportDetail;
